import React from 'react';
import { CloseCircleGreyIcon, ShareFacebookIcon, ShareInstagramIcon, ShareLinkedinIcon, ShareWhatsappIcon, ShareXIcon } from 'assets';
import { copyToClipBoard } from 'helpers';
import { useToast } from 'hooks';

interface ShareJobPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareLink: string;
  shareDraftMessage: string;
}

const ShareJobPostModal: React.FC<ShareJobPostModalProps> = ({
  isOpen,
  onClose,
  shareLink,
  shareDraftMessage,
}) => {
  const { openToast } = useToast();

  const socialShares = [
    {
      key: 'whatsapp',
      label: 'Whatsapp',
      Icon: ShareWhatsappIcon,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      Icon: ShareLinkedinIcon,
    },
    {
      key: 'x',
      label: 'Twitter',
      Icon: ShareXIcon,
    },
    {
      key: 'facebook',
      label: 'Facebook',
      Icon: ShareFacebookIcon,
    },
    {
      key: 'instagram',
      label: 'Instagram',
      Icon: ShareInstagramIcon,
    },
  ] as const;

  if (!isOpen) return null;

  const openShareUrl = (url: string) => {
    const popup = window.open(url, '_blank', 'noopener,noreferrer');

    if (!popup) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Please allow popups to share this job post.',
        type: 'error',
        timeOut: 5000,
      });
    }
  };

  const copyDraftToClipboard = (successText: string) => {
    copyToClipBoard({
      link: shareDraftMessage,
      onSuccess: () => {
        openToast({
          show: true,
          heading: 'Success',
          text: successText,
          type: 'info',
          timeOut: 7000,
        });
      },
      onError: () => {
        openToast({
          show: true,
          heading: 'Error',
          text: 'Failed to copy share draft.',
          type: 'error',
          timeOut: 5000,
        });
      },
    });
  };

  const handleSharePlatform = (platform: 'whatsapp' | 'linkedin' | 'x' | 'facebook' | 'instagram') => {
    if (!shareLink) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Missing application link for this job post.',
        type: 'error',
        timeOut: 5000,
      });
      return;
    }

    if (platform === 'whatsapp') {
      openShareUrl(`https://wa.me/?text=${encodeURIComponent(shareDraftMessage)}`);
      return;
    }

    if (platform === 'linkedin') {
      copyDraftToClipboard('Post text copied! LinkedIn will open — paste the text into the compose box and click Post.');
      openShareUrl(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`);
      return;
    }

    if (platform === 'x') {
      openShareUrl(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareDraftMessage)}`);
      return;
    }

    if (platform === 'facebook') {
      copyDraftToClipboard('Post text copied! Facebook will open — paste the text into the compose box and click Post.');
      openShareUrl(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`);
      return;
    }

    copyDraftToClipboard('Draft copied for Instagram. Paste it into Instagram with the job link.');
  };

  const handleCopyApplicationLink = () => {
    if (!shareLink) return;

    copyToClipBoard({
      link: shareLink,
      onSuccess: () => {
        openToast({
          show: true,
          heading: 'Success',
          text: 'Application link copied to clipboard!',
          type: 'info',
          timeOut: 5000,
        });
      },
      onError: () => {
        openToast({
          show: true,
          heading: 'Error',
          text: 'Failed to copy application link.',
          type: 'error',
          timeOut: 5000,
        });
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-3 py-4 sm:px-4 sm:py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[400px] rounded-[16px] bg-white px-4 py-4 shadow-[0px_24px_80px_rgba(16,24,40,0.22)] sm:px-5 sm:py-5"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#EAECF0] pb-4">
          <div>
            <h2 className="text-[18px] font-medium leading-[1.1] tracking-[-0.03em] text-[#1D2939] sm:text-[20px]">
              Share job post
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#667085]"
            aria-label="Close share modal"
          >
            <CloseCircleGreyIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="pt-4 sm:pt-5">
          <p className="text-[14px] font-normal leading-tight tracking-[-0.02em] text-[#475467] sm:text-[16px]">
            Share to socials or copy link
          </p>

          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {socialShares.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleSharePlatform(key)}
                className="flex flex-col items-center justify-center gap-2 rounded-[6px] bg-[#F2F4F7] px-3 py-2.5 text-[#475467] transition-transform duration-150 hover:-translate-y-0.5 hover:bg-[#F3F4F6]"
              >
                <Icon className="h-6 w-6" />
                <span className="text-[11px] font-medium leading-tight">{label}</span>
              </button>
            ))}
          </div>

          {shareDraftMessage && (
            <div className="mt-4 rounded-[6px] border border-[#EAECF0] bg-[#F9FAFB] p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[12px] font-medium text-[#667085]">Post text</p>
                <button
                  type="button"
                  onClick={() => copyDraftToClipboard('Post text copied to clipboard!')}
                  className="shrink-0 text-[12px] font-medium text-[#012169] hover:underline"
                >
                  Copy text
                </button>
              </div>
              <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-[#344054]">
                {shareDraftMessage}
              </p>
            </div>
          )}

          <div className="mt-4 flex items-stretch sm:gap-8 gap-4 rounded-[6px] border border-[#EAECF0] bg-[#F9FAFB] p-2">
            <div className="min-w-0 flex-1 px-2 py-2">
              <p className="truncate text-[14px] font-medium leading-tight tracking-[-0.02em] text-[#344054] sm:text-[15px]">
                {shareLink || 'No application link available'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleCopyApplicationLink}
              disabled={!shareLink}
              className="shrink-0 rounded-[6px] bg-[#012169] px-4 py-3 text-[13px] font-medium leading-none text-white transition-colors hover:bg-[#091f59] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ShareJobPostModal };